import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import useMediaQuery from '@mui/material/useMediaQuery';
import TutorialDialog from '../tutorial-help/TutorialDialog';
import GuidedTourVideo from './GuidedTourVideo';
import GuidedTourFarewell from './GuidedTourFarewell';
import GuidedTourSummary from './GuidedTourSummary';
import DemoCleanupNudge from './DemoCleanupNudge';
import useShowcaseTab from './useShowcaseTab';
import useTutorialChapters, { lessonsByIds } from '../../pages/system/tutorials/useTutorialChapters';
import { useGuidedTour } from './GuidedTourContext';
import TOUR_SCRIPTS from './tourScripts';
import styles from './GuidedTourOverlay.module.css';

const LOOKUP_MS = 250;
// Depois de salvar, o formulário fecha antes do servidor confirmar: confere rápido por um tempo
const CHECK_FAST_MS = 1000;
const CHECK_WINDOW_MS = 5000;
const BALLOON_WIDTH = 340;
const BALLOON_HEIGHT = 190; // até medir o balão de verdade
const GAP = 14;
const MARGIN = 16;
// Camadas padrão: destaque acima das janelas do MUI (1300), balão logo acima
const SPOTLIGHT_LAYER = 1400;
const BALLOON_LAYER = 1450;
// O que o balão não deve cobrir dentro de uma janela ou painel
const FIELD_SELECTOR = 'input, select, textarea, button, label, [role="button"], [role="combobox"], [contenteditable="true"]';
// O que o balão evita cobrir em qualquer lugar da tela: controles, links e ícones clicáveis
const CONTROL_SELECTOR = `${FIELD_SELECTOR}, a[href], [role="tab"], [role="menuitem"], [role="link"], [role="switch"], [role="checkbox"]`;
const PANEL_SELECTOR = '[role="dialog"], [aria-modal="true"], .MuiDialog-paper, .MuiDrawer-paper';
// Pontos conferidos dentro de cada posição possível do balão (colunas x linhas)
const SAMPLE_COLUMNS = 6;
const SAMPLE_ROWS = 4;

const visibleRect = (target) => {
  // Lista de alvos: vale o primeiro que estiver na tela (o mais preciso primeiro)
  if (Array.isArray(target)) {
    for (let index = 0; index < target.length; index += 1) {
      const found = visibleRect(target[index]);
      if (found) return found;
    }
    return null;
  }
  const element = document.querySelector(`[data-tour="${target}"]`);
  const rect = element ? element.getBoundingClientRect() : null;
  return rect && rect.width > 0 && rect.height > 0 ? { element, rect } : null;
};

/**
 * Maior z-index entre os ancestrais do elemento: quando o alvo está numa janela
 * acima das do MUI (o ConfirmDialog usa 9999), o destaque e o balão sobem junto.
 */
const layerOf = (element) => {
  let layer = 0;
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const value = parseInt(window.getComputedStyle(node).zIndex, 10);
    if (!Number.isNaN(value)) layer = Math.max(layer, value);
  }
  return layer;
};

/** Passo mais adiantado cujo elemento está visível na tela, com a posição dele (e a dos de highlight). */
const findStep = (steps) => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (steps[index].ready && !steps[index].ready()) continue;
    const found = visibleRect(steps[index].target);
    if (found) {
      const extras = (steps[index].highlight || []).map(visibleRect).filter(Boolean).map((entry) => entry.rect);
      return { index, rect: found.rect, extras, element: found.element, layer: layerOf(found.element) };
    }
  }
  return null;
};

const sameRect = (a, b) => a && b && ['top', 'left', 'width', 'height'].every((key) => Math.round(a[key]) === Math.round(b[key]));
const sameRects = (a, b) => a.length === b.length && a.every((rect, index) => sameRect(rect, b[index]));

/**
 * Rola até o alvo: inteiro e no centro quando cabe; alto demais (ou no celular, onde o
 * balão ocupa a base), pelo começo, para a parte que importa ficar à vista.
 */
const scrollToTarget = (element, rect, compact, topInset) => {
  if (compact && topInset) { scrollBelowBalloon(element, topInset); return; }
  const tall = rect.height > window.innerHeight * 0.6;
  element.scrollIntoView({ block: compact || tall ? 'start' : 'center', behavior: 'smooth' });
};

/** Celular, guia de ver: rola até o começo do alvo ficar logo abaixo do balão, que fica no topo. */
function scrollBelowBalloon(element, topInset) {
  const previous = element.style.scrollMarginTop;
  element.style.scrollMarginTop = `${topInset}px`;
  element.scrollIntoView({ block: 'start', behavior: 'smooth' });
  element.style.scrollMarginTop = previous;
}

/** Espaço que o balão ocupa no topo da tela do celular (com a folga até o alvo). */
const COMPACT_TOP = 12;
const compactInset = (height) => COMPACT_TOP + height + GAP;

/**
 * Passo atual e a posição do alvo. Num alvo novo, rola até ele e só mostra o destaque
 * quando a posição para de mudar (fim da rolagem, tela terminando de carregar), para o
 * destaque não piscar onde o alvo estava (settle: só no showcase; nos guias de fazer,
 * sumir por um instante pareceria salvar, veja useSaving). Depois acompanha a rolagem.
 */
function useCurrentStep(steps, compact, settle, insetRef) {
  const [found, setFound] = useState(null);
  const lastIndex = useRef(null);
  const lastElement = useRef(null);
  const lastRect = useRef(null);
  const settled = useRef(false);

  useEffect(() => {
    if (!steps) { setFound(null); return undefined; }
    lastIndex.current = null;
    lastElement.current = null;
    settled.current = !settle;
    const tick = () => {
      const next = findStep(steps);
      if (next && (next.index !== lastIndex.current || next.element !== lastElement.current)) {
        if (next.index !== lastIndex.current) scrollToTarget(next.element, next.rect, compact, insetRef ? insetRef.current : 0);
        settled.current = !settle;
        lastRect.current = null;
      }
      lastIndex.current = next ? next.index : null;
      lastElement.current = next ? next.element : null;
      if (next && !settled.current) {
        const stable = sameRect(lastRect.current, next.rect);
        lastRect.current = next.rect;
        if (!stable) { setFound(null); return; }
        settled.current = true;
      }
      setFound((previous) => {
        if (!next) return null;
        if (previous && previous.index === next.index && sameRect(previous.rect, next.rect)
          && sameRects(previous.extras, next.extras) && previous.element === next.element
          && previous.layer === next.layer) return previous;
        return { index: next.index, rect: next.rect, extras: next.extras, element: next.element, layer: next.layer };
      });
    };
    tick();
    const timer = setInterval(tick, LOOKUP_MS);
    // Acompanha a rolagem sem esperar o próximo intervalo (destaque e balão na posição certa)
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = null; if (settled.current) tick(); });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      clearInterval(timer);
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [steps, compact, settle, insetRef]);

  return found;
}

/**
 * Showcase: enquanto o alvo da parada ainda carrega, o balão espera um pouco em vez de
 * aparecer num canto e pular para perto do destaque. Sem alvo depois disso, aparece.
 */
const SHOWCASE_WAIT_MS = 2000;
function useShowcaseWait(step) {
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (!step || step.summary) { setWaiting(false); return undefined; }
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), SHOWCASE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [step]);
  return waiting;
}

/**
 * No celular o balão ocupa a largura toda: fica na base e sobe para o topo quando o
 * alvo está na metade de baixo da tela, para não cobrir o destaque. No guia de ver
 * (showcase) a tela rola para o alvo ficar abaixo do balão no topo; ele só desce para
 * a base quando o alvo não tem como descer (começo da página) e cabe acima dele.
 */
const compactStyle = (current, height, showcase) => {
  if (!current) return null;
  const top = { top: COMPACT_TOP, bottom: 'auto' };
  if (showcase) return compactCovers(current.rect, height) && fitsAboveBase(current.rect, height) ? null : top;
  const center = current.rect.top + current.rect.height / 2;
  return center > window.innerHeight / 2 ? top : null;
};
// Tolerância de alguns px para a rolagem que para um pouco antes
const compactCovers = (rect, height) => rect.top < compactInset(height) - 4;
const fitsAboveBase = (rect, height) => rect.bottom <= window.innerHeight - compactInset(height);

/**
 * Celular, guia de ver: a primeira rolagem usa a altura do balão da parada anterior.
 * Quando o balão desta aparece, confere uma vez por parada e, se ele ainda cobre o
 * começo do alvo (e não dá para pô-lo na base), rola de novo com a altura real.
 */
function useCompactShowcaseFit(enabled, step, current, balloonRef) {
  const fitted = useRef(null);
  useEffect(() => {
    if (!enabled || !step || !current || !balloonRef.current || fitted.current === step) return;
    fitted.current = step;
    const height = balloonRef.current.offsetHeight;
    if (compactCovers(current.rect, height) && !fitsAboveBase(current.rect, height)) {
      scrollBelowBalloon(current.element, compactInset(height));
    }
  }, [enabled, step, current, balloonRef]);
}

/**
 * Quando a pessoa volta do último passo (o formulário fechou ao salvar),
 * mostra "conferindo" e pergunta ao servidor a cada segundo, em vez de voltar
 * a pedir o primeiro clique. Sem confirmação na janela, segue o passo da tela.
 * Com savedWhenGone no último passo, só confere quando nenhum passo sobrou na
 * tela (a janela fecha antes de gravar e o passo anterior ainda aparece por um
 * instante); voltar mais para trás ali é cancelar, não salvar.
 */
function useSaving(activeKey, current, check, steps) {
  const [saving, setSaving] = useState(false);
  const reached = useRef(-1);
  const index = current ? current.index : -1;

  useEffect(() => {
    reached.current = -1;
    setSaving(false);
  }, [activeKey]);

  useEffect(() => {
    const last = steps ? steps.length - 1 : -1;
    if (index < reached.current && reached.current > 0 && reached.current === last) {
      if (steps[last].savedWhenGone && index !== -1) {
        if (index !== last - 1) reached.current = index;
        return;
      }
      setSaving(true);
      reached.current = index;
    } else if (index < reached.current) {
      reached.current = index;
    } else if (index > reached.current) {
      reached.current = index;
      setSaving(false);
    }
  }, [index, steps]);

  useEffect(() => {
    if (!saving) return undefined;
    check();
    const timer = setInterval(check, CHECK_FAST_MS);
    const done = setTimeout(() => setSaving(false), CHECK_WINDOW_MS);
    return () => { clearInterval(timer); clearTimeout(done); };
  }, [saving, check]);

  return saving;
}

const BLOCKED_EVENTS = ['pointerdown', 'mousedown', 'mouseup', 'click', 'touchstart', 'dblclick', 'contextmenu', 'keydown'];

/**
 * Com a tela escurecida (ou com campos em highlight), só o elemento destacado, os de
 * step.also e step.highlight e o balão respondem: o resto ignora o clique (e o Esc
 * não fecha a janela), para a pessoa não sair do caminho.
 */
function useBlockOutside(step) {
  useEffect(() => {
    if (!step || !(step.dim || step.highlight)) return undefined;
    const targets = [...[].concat(step.target), ...(step.also || []), ...(step.highlight || [])];
    const block = (event) => {
      if (event.type === 'keydown') {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
        return;
      }
      if (event.target.closest && event.target.closest('[data-tour-balloon]')) return;
      const allowed = targets.some((target) => {
        const element = document.querySelector(`[data-tour="${target}"]`);
        return element && element.contains(event.target);
      });
      if (allowed) return;
      event.preventDefault();
      event.stopPropagation();
    };
    BLOCKED_EVENTS.forEach((type) => window.addEventListener(type, block, true));
    return () => BLOCKED_EVENTS.forEach((type) => window.removeEventListener(type, block, true));
  }, [step]);
}

const overlapArea = (a, b) => Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left))
  * Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));

const unionOf = (rects) => rects.reduce((box, rect) => ({
  top: Math.min(box.top, rect.top),
  left: Math.min(box.left, rect.left),
  right: Math.max(box.right, rect.right),
  bottom: Math.max(box.bottom, rect.bottom),
}), { top: Infinity, left: Infinity, right: -Infinity, bottom: -Infinity });

/** Campos visíveis da janela ou painel onde está o alvo (nenhum quando o alvo está na página). */
const fieldRectsAround = (element) => {
  const panel = element && element.closest(PANEL_SELECTOR);
  if (!panel) return [];
  return Array.from(panel.querySelectorAll(FIELD_SELECTOR))
    .map((field) => field.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0
      && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth);
};

/**
 * Peso do que está embaixo de um ponto da tela (fora o próprio balão): controle
 * clicável 3, ícone ou imagem 2 (o X das janelas é um ícone com clique), texto 1
 * (títulos, rótulos, valores), fundo 0.
 */
const pointWeight = (x, y) => {
  if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return 0;
  const node = document.elementsFromPoint(x, y).find((item) => !item.closest('[data-tour-balloon]'));
  if (!node || node === document.body || node === document.documentElement) return 0;
  if (node.closest(CONTROL_SELECTOR)) return 3;
  const media = node.closest('svg, img, video, canvas');
  if (media) return media.tagName.toLowerCase() === 'svg' && window.getComputedStyle(media).cursor !== 'pointer' ? 1 : 2;
  const hasText = Array.from(node.childNodes).some((child) => child.nodeType === 3 && child.textContent.trim());
  return hasText ? 1 : 0;
};

/** Quanto de conteúdo (controles, ícones, textos) o balão cobriria naquela posição, em px². */
const contentUnder = (balloon) => {
  const cell = ((balloon.right - balloon.left) * (balloon.bottom - balloon.top)) / (SAMPLE_COLUMNS * SAMPLE_ROWS);
  let weight = 0;
  for (let column = 0; column < SAMPLE_COLUMNS; column += 1) {
    for (let row = 0; row < SAMPLE_ROWS; row += 1) {
      const x = balloon.left + ((column + 0.5) * (balloon.right - balloon.left)) / SAMPLE_COLUMNS;
      const y = balloon.top + ((row + 0.5) * (balloon.bottom - balloon.top)) / SAMPLE_ROWS;
      weight += pointWeight(x, y);
    }
  }
  return weight * cell;
};

/**
 * Posição do balão: sempre inteiro dentro da janela do navegador e, entre as
 * posições possíveis (abaixo, acima ou ao lado do alvo, ao lado do painel, no
 * rodapé, nos cantos), a que menos cobre o alvo (peso maior: nunca cobrir o que
 * a pessoa precisa clicar), os campos destacados, os campos do formulário aberto
 * e o que está embaixo (botões, ícones como o X da janela, títulos). No empate
 * fica a primeira da lista (a mais perto do alvo). Se nenhuma servir, a pessoa
 * arrasta o balão pela alça do topo.
 */
const balloonPosition = (current, height) => {
  const { rect, extras, element } = current;
  const width = BALLOON_WIDTH;
  const maxLeft = Math.max(MARGIN, window.innerWidth - width - MARGIN);
  const maxTop = Math.max(MARGIN, window.innerHeight - height - MARGIN);
  const clamp = (top, left) => ({
    top: Math.min(Math.max(top, MARGIN), maxTop),
    left: Math.min(Math.max(left, MARGIN), maxLeft),
  });
  const fields = fieldRectsAround(element);
  const focus = [rect, ...extras];
  const box = unionOf([...focus, ...fields]);
  const middle = rect.top + rect.height / 2 - height / 2;

  const candidates = [
    clamp(rect.bottom + GAP, rect.left), // abaixo do alvo
    clamp(rect.top - GAP - height, rect.left), // acima do alvo
    clamp(rect.bottom + GAP, rect.right - width), // abaixo do alvo, alinhado à direita
    clamp(rect.top - GAP - height, rect.right - width), // acima do alvo, alinhado à direita
    clamp(rect.top, rect.right + GAP), // à direita do alvo
    clamp(rect.top, rect.left - GAP - width), // à esquerda do alvo
    clamp(middle, box.left - GAP - width), // ao lado do painel, à esquerda
    clamp(middle, box.right + GAP), // ao lado do painel, à direita
    clamp(rect.bottom - height, rect.left - GAP - width), // à esquerda do alvo, no rodapé
    clamp(rect.bottom - height, box.left), // rodapé do painel, do outro lado
    clamp(box.bottom + GAP, rect.left), // abaixo do painel
    clamp(box.top - GAP - height, rect.left), // acima do painel
    clamp(maxTop, maxLeft),
    clamp(maxTop, MARGIN),
    clamp(MARGIN, maxLeft),
    clamp(MARGIN, MARGIN),
  ];

  let best = null;
  candidates.forEach((position) => {
    const balloon = { top: position.top, left: position.left, right: position.left + width, bottom: position.top + height };
    const cost = focus.reduce((sum, item) => sum + 10 * overlapArea(balloon, item), 0)
      + fields.reduce((sum, item) => sum + overlapArea(balloon, item), 0)
      + contentUnder(balloon);
    if (!best || cost < best.cost) best = { cost, position };
  });
  return best.position;
};

/**
 * Destaque do elemento e balão do guia na tela. Não bloqueia cliques: a pessoa
 * usa a tela normalmente e o balão acompanha o que está aberto.
 */
function GuidedTourOverlay() {
  const tour = useGuidedTour();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const compact = useMediaQuery('(max-width: 600px)');
  const { chapters } = useTutorialChapters();
  const [videoOpen, setVideoOpen] = useState(false);
  // Item de vídeo (cores da agenda): a pessoa fechou a janela da lição
  const [videoClosed, setVideoClosed] = useState(false);
  // Altura real do balão, para posicioná-lo inteiro dentro da tela
  const balloonRef = useRef(null);
  const [balloonHeight, setBalloonHeight] = useState(BALLOON_HEIGHT);
  const isVideo = Boolean(tour.activeKey && TOUR_SCRIPTS[tour.activeKey]?.video);
  const script = tour.activeKey && !tour.finishedKey ? TOUR_SCRIPTS[tour.activeKey] : null;
  // Guia de ver (showcase): só a parada atual conta, e quem avança é o Próximo
  const showcase = script && script.showcase ? tour.showcase : null;
  const showcaseStep = showcase ? showcase.step : null;
  const steps = useMemo(() => {
    if (!script) return null;
    if (script.showcase) return showcaseStep ? [showcaseStep] : null;
    return script.steps;
  }, [script, showcaseStep]);
  const isShowcase = Boolean(script && script.showcase);
  // Altura do balão para a rolagem do celular no guia de ver (ref: não reinicia a busca do alvo)
  const insetRef = useRef(0);
  const current = useCurrentStep(steps, compact, isShowcase, insetRef);
  const saving = useSaving(tour.activeKey, current, tour.check, script && !script.showcase ? steps : null);
  const showIntro = Boolean(script && script.intro && tour.intro);
  const blockStep = steps && current && !saving && !videoOpen && !showIntro ? steps[current.index] : null;
  useBlockOutside(blockStep);
  useShowcaseTab(showcaseStep, pathname);
  const showcaseWaiting = useShowcaseWait(showcaseStep);
  useEffect(() => setVideoClosed(false), [tour.activeKey]);
  useLayoutEffect(() => {
    const height = balloonRef.current ? balloonRef.current.offsetHeight : 0;
    if (height && Math.abs(height - balloonHeight) > 2) setBalloonHeight(height);
  }, [balloonHeight, current, pathname, tour.activeKey]);
  insetRef.current = compact && isShowcase ? compactInset(balloonHeight) : 0;
  useCompactShowcaseFit(compact && isShowcase, showcaseStep, current, balloonRef);
  // Posição escolhida pela pessoa ao arrastar o balão; volta ao automático a cada passo
  const [dragged, setDragged] = useState(null);
  const stepIndex = current ? current.index : -1;
  useEffect(() => setDragged(null), [tour.activeKey, stepIndex]);
  const lessonId = tour.item?.lesson || null;
  const lessons = useMemo(() => (lessonId ? lessonsByIds(chapters, [lessonId]) : []), [chapters, lessonId]);

  // Na configuração inicial o guia não aparece (ele começa na tela final dela)
  if (!tour.activeKey || pathname.startsWith('/setup-wizard')) return null;

  const autoStyle = current ? balloonPosition(current, balloonHeight) : { right: 24, bottom: 24 };
  const fixedStyle = compact ? compactStyle(current, balloonHeight, isShowcase) : (dragged || autoStyle);
  const layer = current ? current.layer : 0;

  /** Arrasta o balão pela alça do topo, sempre inteiro dentro da tela. */
  const startDrag = (event) => {
    if (compact || !balloonRef.current || event.button > 0) return;
    event.preventDefault();
    const box = balloonRef.current.getBoundingClientRect();
    const offsetX = event.clientX - box.left;
    const offsetY = event.clientY - box.top;
    const move = (moveEvent) => setDragged({
      top: Math.min(Math.max(moveEvent.clientY - offsetY, MARGIN), Math.max(MARGIN, window.innerHeight - box.height - MARGIN)),
      left: Math.min(Math.max(moveEvent.clientX - offsetX, MARGIN), Math.max(MARGIN, window.innerWidth - box.width - MARGIN)),
    });
    const stopDrag = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stopDrag);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stopDrag);
  };

  // Item de vídeo: a lição abre logo depois da apresentação, só com o vídeo e o
  // Próximo, que conclui o passo mesmo sem dar play
  if (isVideo && !videoClosed && !showIntro && lessons.length > 0) {
    const closeVideo = () => {
      setVideoClosed(true);
      tour.check();
    };
    return <GuidedTourVideo lesson={lessons[0]} onNext={closeVideo} />;
  }

  if (tour.finishedKey) {
    const doneCount = tour.items.filter((entry) => entry.done).length;
    const doneCopy = TOUR_SCRIPTS[tour.finishedKey]?.done;
    return createPortal(
      <div className={styles.backdrop}>
        <div className={styles.celebrate} role="dialog" aria-modal="true" aria-label="Passo concluído">
          <span className={styles.celebrateIcon}><CheckCircleIcon /></span>
          <span className={styles.celebrateCount}>Passo {doneCount} de {tour.items.length} concluído</span>
          <div className={styles.celebrateSteps} aria-hidden="true">
            {tour.items.map((entry) => (
              <span key={entry.key} className={entry.done ? styles.stepDone : ''} />
            ))}
          </div>
          <strong className={styles.celebrateTitle}>{doneCopy?.title || tour.item?.label || 'Passo concluído'}</strong>
          {doneCopy && <p className={styles.celebrateText}>{doneCopy.text}</p>}
          {tour.next ? (
            <>
              <div className={styles.nextBox}>
                <span className={styles.nextLabel}>Agora vamos</span>
                <strong>{tour.next.label}</strong>
                <span className={styles.nextText}>{tour.next.description}</span>
              </div>
              <div className={styles.celebrateActions}>
                <button type="button" className={styles.linkButton} onClick={tour.stop}>Parar por aqui</button>
                <button type="button" className={styles.primaryButton} onClick={() => tour.start(tour.next.key)}>
                  Continuar <ArrowForwardIcon fontSize="small" />
                </button>
              </div>
            </>
          ) : (
            // Último passo: encerramento que leva sozinho ao Início
            <GuidedTourFarewell onGo={() => { tour.stop(); navigate('/dashboard'); }} />
          )}
        </div>
      </div>,
      document.body
    );
  }

  // Apresentação do item: onde a pessoa está e o que vamos fazer, antes do primeiro destaque
  if (showIntro) {
    const position = tour.items.findIndex((entry) => entry.key === tour.activeKey) + 1;
    return createPortal(
      <div className={styles.backdrop}>
        <div className={styles.celebrate} role="dialog" aria-modal="true" aria-label={script.intro.title}>
          {position > 0 && (
            <>
              <span className={styles.celebrateCount}>Passo {position} de {tour.items.length}</span>
              <div className={styles.celebrateSteps} aria-hidden="true">
                {tour.items.map((entry) => (
                  <span key={entry.key} className={entry.done || entry.key === tour.activeKey ? styles.stepDone : ''} />
                ))}
              </div>
            </>
          )}
          <strong className={styles.celebrateTitle}>{script.intro.title}</strong>
          <p className={styles.celebrateText}>{(compact && script.intro.textCompact) || script.intro.text}</p>
          {script.intro.menu && (
            <div className={styles.nextBox}>
              <span className={styles.nextLabel}>Para voltar aqui depois, no menu</span>
              <strong>{script.intro.menu.join(' › ')}</strong>
            </div>
          )}
          <div className={styles.celebrateActions}>
            <button type="button" className={styles.linkButton} onClick={tour.stop}>Sair do guia</button>
            <button type="button" className={styles.primaryButton} onClick={tour.closeIntro}>
              Vamos lá <ArrowForwardIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Passou da aula pelo Próximo: aguarda o servidor confirmar o passo (a comemoração
  // entra no lugar); se demorar, dá para rever o vídeo ou sair
  if (isVideo) {
    if (!videoClosed) return null;
    return createPortal(
      <div className={styles.backdrop}>
        <div className={styles.celebrate} role="alertdialog" aria-modal="true" aria-busy="true" aria-label={tour.item?.label || 'Vídeo'}>
          <span className={styles.savingSpinner} aria-hidden="true" />
          <strong className={styles.celebrateTitle}>Só um instante</strong>
          <p className={styles.celebrateText}>Concluindo este passo.</p>
          <div className={styles.celebrateActions}>
            <button type="button" className={styles.linkButton} onClick={tour.stop}>Sair do guia</button>
            <button type="button" className={styles.videoButton} onClick={() => setVideoClosed(false)}>
              <PlayCircleOutlineIcon fontSize="small" /> Ver o vídeo de novo
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Conferindo o salvamento: janela no centro que bloqueia a tela até o servidor responder
  if (saving) {
    return createPortal(
      <div className={styles.backdrop}>
        <div className={styles.celebrate} role="alertdialog" aria-modal="true" aria-busy="true" aria-label="Conferindo">
          <span className={styles.savingSpinner} aria-hidden="true" />
          <strong className={styles.celebrateTitle}>Só um instante</strong>
          <p className={styles.celebrateText}>Conferindo se ficou tudo salvo.</p>
        </div>
      </div>,
      document.body
    );
  }

  let content;
  if (script && script.showcase) {
    if (!showcase) return null;
    const step = showcase.step;
    // Parada de resumo: cartão no centro, sem alvo na tela
    if (step.summary && pathname === step.path) {
      return (
        <GuidedTourSummary
          step={step}
          eyebrow={`Guia · ${script.label} · ${showcase.index + 1} de ${showcase.total}`}
          onNext={tour.advance}
          onStop={tour.stop}
        />
      );
    }
    const elsewhere = !current && pathname !== step.path;
    if (!current && !elsewhere && showcaseWaiting) return null;
    content = (
      <>
        <div
          className={`${styles.dragHandle} ${compact ? styles.dragHandleOff : ''}`}
          onPointerDown={startDrag}
          title={compact ? undefined : 'Arraste para mover o balão'}
        >
          <span className={styles.eyebrow}>Guia · {script.label} · {showcase.index + 1} de {showcase.total}</span>
          {!compact && <DragIndicatorIcon className={styles.dragIcon} fontSize="small" aria-hidden="true" />}
        </div>
        <strong className={styles.title}>{elsewhere ? 'Vamos para a próxima parada' : step.title}</strong>
        <p className={styles.text}>{elsewhere ? 'O guia continua de onde você parou.' : ((compact && step.textCompact) || step.text)}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.linkButton} onClick={tour.stop}>Sair do guia</button>
          {elsewhere ? (
            <button type="button" className={styles.primaryButton} onClick={() => navigate(step.path)}>
              Continuar
            </button>
          ) : (
            <button type="button" className={styles.primaryButton} onClick={tour.advance}>
              {step.next || 'Próximo'} <ArrowForwardIcon fontSize="small" />
            </button>
          )}
        </div>
      </>
    );
  } else if (script) {
    const step = script.steps[current ? current.index : 0];
    const elsewhere = !current && pathname !== script.path;
    content = (
      <>
        <div
          className={`${styles.dragHandle} ${compact ? styles.dragHandleOff : ''}`}
          onPointerDown={startDrag}
          title={compact ? undefined : 'Arraste para mover o balão'}
        >
          <span className={styles.eyebrow}>Guia · {tour.item?.label || 'Primeiros passos'}</span>
          {!compact && <DragIndicatorIcon className={styles.dragIcon} fontSize="small" aria-hidden="true" />}
        </div>
        <strong className={styles.title}>{elsewhere ? `Vamos para ${script.pathLabel}` : ((compact && step.titleCompact) || step.title)}</strong>
        <p className={styles.text}>{elsewhere ? 'O guia continua de onde você parou.' : ((compact && step.textCompact) || step.text)}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.linkButton} onClick={tour.stop}>Sair do guia</button>
          {lessons.length > 0 && (
            <button type="button" className={styles.videoButton} onClick={() => setVideoOpen(true)}>
              <PlayCircleOutlineIcon fontSize="small" /> Ver o vídeo
            </button>
          )}
          {elsewhere && (
            <button type="button" className={styles.primaryButton} onClick={() => navigate(script.path)}>
              Ir para {script.pathLabel}
            </button>
          )}
          {!elsewhere && current && step.confirm && (
            <button type="button" className={styles.primaryButton} onClick={tour.markSeen}>
              {step.confirm}
            </button>
          )}
        </div>
      </>
    );
  } else {
    return null;
  }

  const step = steps && current && !saving ? steps[current.index] : null;

  return createPortal(
    <>
      {step && !videoOpen && (
        <div
          className={`${styles.spotlight} ${step.dim ? styles.dim : ''}`}
          style={{
            top: current.rect.top - 6,
            left: current.rect.left - 6,
            width: current.rect.width + 12,
            height: current.rect.height + 12,
            zIndex: Math.max(SPOTLIGHT_LAYER, layer + 1),
          }}
        />
      )}
      {step && !videoOpen && current.extras.map((rect, index) => (
        <div
          key={index}
          className={styles.spotlight}
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            zIndex: Math.max(SPOTLIGHT_LAYER, layer + 1),
          }}
        />
      ))}
      {!videoOpen && (
      <div
        className={`${styles.balloon} ${compact ? styles.balloonCompact : ''}`}
        ref={balloonRef}
        style={{ ...fixedStyle, zIndex: Math.max(BALLOON_LAYER, layer + 2) }}
        role="dialog"
        aria-label="Guia dos primeiros passos"
        data-tour-balloon=""
      >
        {content}
      </div>
      )}
      {lessons.length > 0 && (
        <TutorialDialog open={videoOpen} onClose={() => setVideoOpen(false)} lessons={lessons} title="Primeiros passos" />
      )}
    </>,
    document.body
  );
}

/** Camada global do guia (App.js): balão e destaque, e o aviso de tirar os exemplos. */
function GuidedTourLayer() {
  return (
    <>
      <GuidedTourOverlay />
      <DemoCleanupNudge />
    </>
  );
}

export default GuidedTourLayer;
